import { MenuGroup } from '@atlaskit/menu';
import { PopupProps, TriggerProps } from '@atlaskit/popup';
import Tooltip from '@atlaskit/tooltip';
import Popup from '@databases/atlaskit/Popup';
import { messages } from '@databases/intl/messages';
import { useComponent } from '@databases/react';
import { DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { selectSelectedFieldIds } from '@databases/redux/cellSelections/selectors';
import { getDatabaseFieldRequired } from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, {
  DetailedHTMLProps,
  FunctionComponent,
  HTMLAttributes,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';

import DatabaseQuickCalcValueEdit from './DatabaseQuickCalcValueEdit';
import { TriggerButton } from './quickCalcUtils';

type TriggerPropsWithHtmlProps = TriggerProps &
  DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

type QuickCalculationValuesProps = {
  contentId: string;
  scope: string;
  removeSelectedContent: () => void;
  Trigger?: (props: TriggerPropsWithHtmlProps) => React.ReactElement;
  isOpen: boolean;
  offset?: [number, number];
  placement?: PopupProps['placement'];
  onSelect: () => void;
};

const QuickCalculationValues: FunctionComponent<QuickCalculationValuesProps> = ({
  contentId,
  scope,
  Trigger,
  isOpen,
  offset,
  placement,
  onSelect,
}) => {
  const firstSelectedField = defaultStoreUtils.useEqualSelector(
    (state: DefaultRootState) => {
      const database = selectDatabaseByContentIdRequired(state, {
        contentId,
      });
      const selectedFields = selectSelectedFieldIds(state, {
        contentId,
      });

      return getDatabaseFieldRequired(database, selectedFields[0]);
    }
  );

  const [isMenuOpen, setIsMenuOpen] = useState(isOpen);
  const handleToggleIsOpen = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();
      setIsMenuOpen(!isMenuOpen);
    },
    [isMenuOpen, setIsMenuOpen]
  );

  useEffect(() => {
    setIsMenuOpen(isOpen);
  }, [isOpen]);

  const { formatMessage } = useIntl();
  const trigger: PopupProps['trigger'] = useComponent(
    ({ ref, ...props }: TriggerProps) => {
      return Trigger && typeof trigger === 'function' ? (
        Trigger({
          ref,
          ...props,
        })
      ) : (
        <Tooltip content={formatMessage(messages['bulk-edit-values.tooltip'])}>
          <TriggerButton
            {...props}
            spacing="compact"
            appearance="subtle"
            selected={isMenuOpen}
            onClick={(e) => {
              handleToggleIsOpen(e);
            }}
            onMouseEnter={() => setIsMenuOpen(true)}
            ref={ref}
            isActive={isMenuOpen}
          />
        </Tooltip>
      );
    },
    [Trigger, formatMessage, handleToggleIsOpen, isMenuOpen]
  );

  return (
    <Popup
      id="quick-calc-menu"
      autoFocus={false}
      isOpen={isMenuOpen}
      onClose={() => setIsMenuOpen(false)}
      trigger={trigger}
      placement={placement}
      contentContainer={null}
      offset={offset}
      content={({ setInitialFocusRef }) => (
        <MenuGroup
          minWidth="200px"
          maxHeight="435px"
          onClick={(e) => e.stopPropagation()}
        >
          <DatabaseQuickCalcValueEdit
            field={firstSelectedField}
            contentId={contentId}
            scope={scope}
            ref={setInitialFocusRef}
            onClose={() => {
              setIsMenuOpen(false);
              onSelect();
            }}
          />
        </MenuGroup>
      )}
    />
  );
};

export default QuickCalculationValues;
